<template>
    <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
            <h3 class="title-small text-valence-grey-850">{{ title }}</h3>
            <button type="button" aria-label="Close" class="text-gray-400 hover:text-gray-600 transition-colors hover:cursor-pointer" @click="handleDismiss">
                <i class="bi bi-x-lg text-xl" aria-hidden="true" />
            </button>
        </div>
        <template v-if="showIssuesList">
            <!-- sr-only keeps this persistent live region out of layout (it would otherwise be
                 an empty flex item adding a permanent gap); it announces load failures -->
            <span class="sr-only" role="status" aria-live="polite">{{ issuesLoadFailed ? "We couldn't load the feedback options." : "" }}</span>
            <SkeletonLoader v-if="isFetching" class="h-10 w-full" />
            <p v-else-if="issuesLoadFailed" class="details text-valence-grey-700">
                We couldn't load the feedback options.
                <button ref="retryButton" type="button" class="text-valence-primary-500 hover:underline cursor-pointer" @click="retryLoadIssues">Try again</button>
            </p>
            <div v-else ref="issuesGrid" class="flex flex-col gap-2">
                <p class="details text-valence-grey-700">Select all that apply</p>
                <div class="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                    <label v-for="issue in issues" :key="issue" :for="`feedback-issue-${issue}`" class="body-small flex cursor-pointer items-center gap-3 text-valence-grey-600">
                        <input
                            :id="`feedback-issue-${issue}`"
                            v-model="selectedIssues"
                            type="checkbox"
                            :value="issue"
                            class="h-5 w-5 shrink-0 cursor-pointer rounded-md border border-valence-grey-250 accent-valence-primary-500" />
                        {{ issue }}
                    </label>
                </div>
            </div>
        </template>

        <BaseTextarea
            v-if="showCommentInput"
            v-model:input="comment"
            aria-label="Additional comments"
            class="resize-none rounded-lg border-2 border-valence-grey-250 p-4 transition-colors focus:border-valence-primary-300 focus:outline-none"
            rows="3"
            placeholder="Additional comments (optional)" />

        <div class="flex justify-end">
            <BaseButton size="sm" theme="primary" :disabled="!hasChanges" @click="handleSave">Save</BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { getFeedbackIssues } from "~vue/Feedback/feedbackIssuesCache";
import type { FeedbackTarget } from "~vue/types/feedback";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onMounted, ref, useTemplateRef } from "vue";

interface Props {
    target: FeedbackTarget;
    title?: string;
    showIssuesList?: boolean;
    showCommentInput?: boolean;
    initialSelectedIssues?: string[];
    initialComment?: string;
}

const props = withDefaults(defineProps<Props>(), {
    title: "What went wrong?",
    showIssuesList: true,
    showCommentInput: true,
});

const emit = defineEmits<{
    save: [{ issues: string[] | null; comments: string | null }];
    dismiss: [];
}>();

const selectedIssues = ref<string[]>([]);
const comment = ref<string>("");

const issues = ref<string[]>([]);
const isFetching = ref(true);
const issuesLoadFailed = ref(false);

async function loadIssues() {
    isFetching.value = true;
    issuesLoadFailed.value = false;
    try {
        issues.value = await getFeedbackIssues(props.target);
    } catch {
        issuesLoadFailed.value = true;
    } finally {
        isFetching.value = false;
    }
}

const retryButton = useTemplateRef("retryButton");
const issuesGrid = useTemplateRef("issuesGrid");

// Retry swaps the focused button for the skeleton, so keyboard/screen-reader users lose their
// place — restore focus to the new Try again on failure, or the first option on success.
// Only retry manages focus; the mount-time load keeps the dialog's own focus behavior.
async function retryLoadIssues() {
    await loadIssues();
    await nextTick();
    if (issuesLoadFailed.value) {
        retryButton.value?.focus();
    } else {
        issuesGrid.value?.querySelector("input")?.focus();
    }
}

onMounted(() => {
    if (props.initialSelectedIssues) {
        selectedIssues.value = props.initialSelectedIssues;
    }
    if (props.initialComment) {
        comment.value = props.initialComment;
    }
    logUserInteraction(AmplitudeEventNames.FEEDBACK_ISSUES_FORM_SHOWN, {
        target: props.target,
    });
    void loadIssues();
});

const hasChanges = computed(() => {
    const initialIssues = props.initialSelectedIssues ?? [];
    const issuesChanged = selectedIssues.value.length !== initialIssues.length || selectedIssues.value.some((issue) => !initialIssues.includes(issue));
    const commentChanged = comment.value.trim() !== (props.initialComment ?? "").trim();
    return issuesChanged || commentChanged;
});

function handleSave() {
    logUserInteraction(AmplitudeEventNames.FEEDBACK_ISSUES_FORM_SAVED, {
        selectedIssues: selectedIssues.value,
        comment: comment.value,
        target: props.target,
    });
    emit("save", {
        issues: props.showIssuesList && selectedIssues.value.length > 0 ? selectedIssues.value : null,
        comments: props.showCommentInput && comment.value.trim() ? comment.value.trim() : null,
    });
}

function handleDismiss() {
    logUserInteraction(AmplitudeEventNames.FEEDBACK_ISSUES_FORM_DISMISSED, {
        target: props.target,
    });
    emit("dismiss");
}
</script>
